import React, { useState, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './login_style.scss';
import appLogo from '../../../assets/anytrakk.png';
import { getLoginUser, getOTP, getRoleFeatures } from './action.ts';

const LoginPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [pin, setPin] = useState(['', '', '', '']);
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isNumericInput = (value: string) => /^[0-9]*$/.test(value);
  const [mobileError, setMobileError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const createInputRefArray = (length: number) => {
    return Array.from({ length }, () => React.createRef<HTMLInputElement>());
  };
  
  const otpInputRefs = createInputRefArray(otp.length);
  const pinInputRefs = createInputRefArray(pin.length);
  const navigate = useNavigate();

  useEffect(() => {
    const isPinComplete = pin[3] !== '' && pin.every(digit => digit !== '');
    const isOtpComplete = otp[3] !== '' && otp.every(digit => digit !== '');
    
    if (isPinComplete || isOtpComplete) {
      setShowLoginButton(true);    
    } else {
      setShowLoginButton(false);
    }
  }, [pin, otp, phoneNumber, mobileError]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value); 
    if (value === '') {
      setMobileError('Mobile number is required');
    } else if (!/^\d*$/.test(value)) {
      setMobileError('Phone number must contain only digits');
    } else if (/^[0-5]/.test(value)) {
      setMobileError('Phone number must start with 6, 7, 8, or 9');
    } else if (value.length > 10) {
      setMobileError('Phone number must be 10 digits');
    } else {
      setMobileError('');
    }
  };

  const handlePhoneNumberBlur = () => {
    if (phoneNumber.length < 10 && phoneNumber.length > 0) {
      setMobileError('Phone number must be 10 digits'); 
    }
  };

  const handleOtpChange = (index: number, value: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);    
    if (value !== '' && index < otp.length - 1) {
      otpInputRefs[index + 1].current?.focus();
    }   
    if (value === '' && index > 0 && e.nativeEvent instanceof InputEvent) {
      const inputEvent = e.nativeEvent as InputEvent;
      if (inputEvent.inputType === 'deleteContentBackward') {
        otpInputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePinChange = (index: number, value: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);  
    if (value !== '' && index < pin.length - 1) {
      pinInputRefs[index + 1].current?.focus();
    }  
    if (value === '' && index > 0 && e.nativeEvent instanceof InputEvent) {
      const inputEvent = e.nativeEvent as InputEvent;
      if (inputEvent.inputType === 'deleteContentBackward') {
        pinInputRefs[index - 1].current?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otp[index] === '' && index > 0) {
        e.preventDefault();
        otpInputRefs[index - 1].current?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      otpInputRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowRight' && index < otp.length - 1) {
      e.preventDefault();
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (pin[index] === '' && index > 0) {
        e.preventDefault();
        pinInputRefs[index - 1].current?.focus();
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      pinInputRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowRight' && index < pin.length - 1) {
      e.preventDefault();
      pinInputRefs[index + 1].current?.focus();
    }
  };

  const handleAutoLogin = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    const loginInput = {
      phone: phoneNumber,
      password: pin.join(''),
      language: 'EN',
      country_code: '91',
      otp: otp.join(''),
    };
    
    const successCallBack = (response: any) => {
      setIsSubmitting(false);
      if (response.status === 200) {
        localStorage.setItem("userdetails", JSON.stringify(response));
        localStorage.setItem(
          "trackyo_user",
          JSON.stringify({
            token: response.token,
            userRole: response.role,
            userName: response.name || response.username || "", 
            userPhone: response.phone || phoneNumber || "", 
          })
        );    
        const proceedAfterFeatures = () => {
          toast.success('Login successful');
          const userRole = response.role?.toLowerCase();
          if (userRole === 'platform_tech' || userRole === 'platform_ops' || userRole === 'platform_finance') {
            navigate('/company');
          } else if (userRole === 'vendor_supervisor' || userRole === 'vendor_company_admin') {
            navigate('/dashboard');
          } else if (userRole === 'client_company_admin' || userRole === 'client_supervisor') {
            navigate('/dashboard');
          }
        };

        getRoleFeatures(() => {
          proceedAfterFeatures();
        });
      }
    };
    const errorCallBack = (errMessage: any) => {
      setIsSubmitting(false);
      if (errMessage.response?.status === 400) {
        toast.error(errMessage.message);
      } else {
        toast.error(errMessage.message);
      }
    };
    
    getLoginUser(loginInput, successCallBack, errorCallBack);
  };

  const handleLogin = () => {
    if (!phoneNumber) {
      setMobileError('Mobile number is required');
      toast.error('Mobile number is required', { toastId: 'mobile-error' });
      return;
    }
    if (mobileError) {
      toast.error(mobileError, { toastId: 'mobile-error' });
      return;
    }
    if ((pin.some((digit) => digit === '') || pin.length !== 4) && (otp.some((digit) => digit === '') || otp.length !== 4)) {
      setPasswordError('Enter valid PIN or OTP');
      toast.error('Enter valid PIN or OTP');
      return;
    }
    handleAutoLogin();
  };

  const handleLoginSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleLogin();
  };

  const handleGetOTPClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!phoneNumber) {
      setMobileError('Mobile number is required');
      toast.error('Mobile number is required');
      return;
    }
    else {
      const OTPInput = {
        phone_number: phoneNumber,
        country_code: '+91',
        from_register: 0
      };
      
      const successCallBack = (response: any) => {
        if (response.status === 200) {
          toast.success(response.message); 
        }
      };
      
      const errorCallBack = (errMessage: any) => {
        if (errMessage.response?.status === 400) {
          toast.error(errMessage.message);
        } else {
          toast.error(errMessage.message);
        }
      };
      
      getOTP(OTPInput, successCallBack, errorCallBack);
    }
  };

  const handleHiddenPasswordAutofill = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    const newPin = ['', '', '', ''];
    for (let i = 0; i < val.length; i++) {
      newPin[i] = val[i];
    }
    setPin(newPin);
  };

  return (
    <div className='body'>
      <form className="login-form" style={{justifyContent:"center"}} onSubmit={handleLoginSubmit}>
        <div className="logo-container">
          <img src={appLogo} alt="App Logo" className="app-logo" height={'200px'} width={'auto'} />
        </div>      
        <div className="input-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div className="country-code-input">
            <input
              type="text"
              value="+91"
              readOnly
            />
          </div>
          <label className="otp">
            <input
              type="tel"
              name="phone"             
              autoComplete="tel"
              value={phoneNumber}
              placeholder="Mobile Number"
              onChange={handlePhoneNumberChange}
              onBlur={handlePhoneNumberBlur}
              style={{ height: '2.6em', width: '16.8em' }}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </label>
        </div>
        <div>
          {mobileError && <p className="error-message">{mobileError}</p>}
        </div>

        <input 
          type="password" 
          name="password" 
          autoComplete="current-password" 
          value={pin.join('')} 
          onChange={handleHiddenPasswordAutofill}
          style={{ position: 'absolute', opacity: 0, height: 0, width: 0, border: 'none', zIndex: -1 }} 
          tabIndex={-1} 
        />

        <div className="input-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <p className="pin-heading" style={{marginRight:'20px', color:'white'}}>Enter Your PIN</p>
          <div className="pin-input-fields" style={{ display: 'flex', flexDirection: 'row' }}>
            {pin.map((digit, index) => (
              <div key={index}>
                <input
                  ref={pinInputRefs[index]}
                  type="password"
                  maxLength={1}
                  value={digit}
                  autoComplete="off"
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (isNumericInput(inputValue)) {
                      handlePinChange(index, inputValue, e);
                    }
                  }}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  className="input-digit"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            ))}
          </div>
        </div>
        
        <div className="partition">
          <div className="outer-circle">
            <p className="or-text">OR</p>
          </div>
          <div className="partition-line"></div>
        </div>
        
        <div className="input-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <button className='get-otp' onClick={handleGetOTPClick}>
            <p className="otp-heading">Get Your OTP</p>
          </button>
          <div className="otp-input-fields" style={{ display: 'flex', flexDirection: 'row' }}>
            {otp.map((digit, index) => (
              <div key={index}>
                <input
                  ref={otpInputRefs[index]}
                  type="text"
                  maxLength={1}
                  value={digit}
                  autoComplete="one-time-code"
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    if (isNumericInput(inputValue)) {
                      handleOtpChange(index, inputValue, e);
                    }
                  }}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="input-digit"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            ))}
          </div>
        </div>
        
        {showLoginButton && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', marginBottom: '10px' }}>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{
                background: 'white', 
                color: 'black',
                padding: '10px 40px',
                border: 'none',
                borderRadius: '5px',
                fontSize: '18px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
          </div>
        )}

        <div>
          <label className="policy-text">
            <a href="/privacy_policy" className="policy-link" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            &nbsp;and&nbsp;
            <a
            href="/terms_of_service"
            className="policy-link"
            target="_blank"
            rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            &nbsp;apply.
          </label>
        </div>
      </form>
    </div>
  );
};

export default LoginPage;